import React from 'react'
// icons
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
// components
import { Button, BUTTON_LAYOUTS } from 'components/UI'
// themes
import { colors } from 'themes/index'
// utils
import { reportError } from 'utils/errors/handlers'
import { normalizeError } from 'utils/errors/normalize'
// views
import { FallbackText, FallbackTitle, FallbackWrapper } from './views'

interface Props {
  // where the boundary sits, for the log
  source: string
  // the fallback takes the whole screen instead of the parent's area
  fullScreen?: boolean
  // the boundary recovers when this value changes (e.g. the user navigated to another page)
  resetKey?: string
  children?: React.ReactNode
}

interface State {
  hasError: boolean
}

const reloadPage = () => window.location.reload()

// Catches errors thrown while rendering: without it React unmounts the whole app and leaves a blank screen.
// Request errors do not get here — they are handled by the Apollo ErrorLink.
export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false }

  static getDerivedStateFromError(): State {
    return { hasError: true }
  }

  componentDidCatch(error: unknown, info: React.ErrorInfo) {
    reportError(normalizeError(error), `ErrorBoundary.${this.props.source}`)
    console.error(info.componentStack)
  }

  componentDidUpdate(prevProps: Props) {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false })
    }
  }

  render() {
    if (!this.state.hasError) {
      return this.props.children
    }

    return (
      <FallbackWrapper fullScreen={this.props.fullScreen} role='alert'>
        <ErrorOutlineIcon sx={{ fontSize: 96, marginBottom: 2, color: colors.purple300 }} />
        <FallbackTitle>Something went wrong</FallbackTitle>
        <FallbackText>
          This page could not be displayed. Please reload it. If the problem persists, contact your administrator.
        </FallbackText>
        <Button layout={BUTTON_LAYOUTS.PURPLE} onClick={reloadPage}>
          Reload page
        </Button>
      </FallbackWrapper>
    )
  }
}
